import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { Lightning, ShoppingBag } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { useScrollLock } from '@/lib/useScrollLock'
import { cn } from '@/lib/utils'
import { iconChipClass, dangerBannerClass, infoBannerClass, bannerTextStyle } from '@/lib/styleKit'
import { formatCents } from '@/lib/billingFormat'

/**
 * The confirmation step in front of anything that spends credits or money.
 *
 * Two modes, one shape (the props are a superset, so the original
 * credit-estimate contract still works verbatim):
 *
 * - **Spend** — pass `estimatedCredits`. Shows the cost, the balance, and gates
 *   Confirm behind sufficiency.
 * - **Buy** — pass `priceCents` (and usually `grantedCredits`). Shows the exact
 *   price, what it grants, and states in words that the charge is one-time. No
 *   sufficiency gate: a purchase *adds* credits.
 *
 * Both may be omitted, for an act that changes state without costing anything
 * (activating a pass the user already owns — a free queue reorder).
 *
 * A11y contract per `design-aesthetics.md` §16/§29/§59: portalled to
 * `document.body` at `z-[100]`, `role="dialog"` + `aria-modal`, labelled and
 * described by id, blur-only backdrop as its own `aria-hidden` layer, Escape to
 * dismiss, Tab trapped, focus restored to the trigger on close, background
 * scroll locked. Before this it was a bare `z-50` div with none of that — and
 * zero importers.
 */

/** Everything inside the dialog a keyboard user can land on (§16 focus trap). */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

export interface CreditConfirmModalProps {
  isOpen: boolean
  /** What is about to happen — "Buy 5,000 credits", "Generate 40 emails". */
  actionLabel: string
  /** Credits the action will SPEND. Omit for a purchase. */
  estimatedCredits?: number
  /** Credits the purchase will GRANT. Omit for a spend. */
  grantedCredits?: number
  /** Exact price in cents. Presence of this switches on the money copy. */
  priceCents?: number
  /** ISO currency for `priceCents`. Defaults to USD (the API sends no currency). */
  currency?: string
  /** Spendable balance today. */
  currentBalance: number
  estimatedTokens?: number
  /** One extra sentence of context — what the charge buys, what happens next. */
  detail?: React.ReactNode
  confirmLabel?: string
  /** True while the redirect/request is in flight — blocks a double-fire. */
  loading?: boolean
  /**
   * True when the purchase opens a RECURRING agreement (a plan). Switches the
   * copy from "one-time charge" to the monthly terms. Omit for credit
   * top-ups, which are always a single charge — that is the difference
   * between the two products.
   */
  recurring?: boolean
  /** Charge cadence for a recurring purchase: 'month' (default) bills monthly,
   *  'year' bills once a year. Only read when `recurring` is true. */
  recurringInterval?: 'month' | 'year'
  onConfirm: () => void
  onCancel: () => void
}

export default function CreditConfirmModal({
  isOpen,
  actionLabel,
  estimatedCredits,
  grantedCredits,
  priceCents,
  currency = 'USD',
  currentBalance,
  estimatedTokens,
  detail,
  confirmLabel,
  loading = false,
  recurring = false,
  recurringInterval = 'month',
  onConfirm,
  onCancel,
}: CreditConfirmModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)
  /** Whatever had focus before we opened, so we can hand it back on close. */
  const restoreRef = useRef<HTMLElement | null>(null)
  const uid = useId()
  const titleId = `${uid}-title`
  const descId = `${uid}-desc`

  // Derived above the hooks, not below the early return: the autofocus effect
  // depends on `insufficient`, and a `const` declared after it would be in its
  // temporal dead zone when the dependency array is evaluated.
  const isPurchase = typeof priceCents === 'number' && Number.isFinite(priceCents)
  const isSpend = typeof estimatedCredits === 'number' && Number.isFinite(estimatedCredits)
  // Only a *spend* can be short. A purchase adds credits, so it is never gated.
  const insufficient = isSpend && currentBalance < (estimatedCredits as number)
  // Monthly terms replace the one-time copy for a plan. A free activation
  // (no price) has nothing to bill, so it says neither.
  const showRecurringTerms = isPurchase && recurring

  useScrollLock(isOpen)

  // Focus restore (§16) — captured on open, handed back on close/unmount.
  // Keyed on `isOpen` alone so a re-render for `loading` can never re-capture a
  // node that lives inside the dialog and is about to be destroyed.
  useEffect(() => {
    if (!isOpen) return
    restoreRef.current = (document.activeElement as HTMLElement | null) ?? null
    return () => {
      const previous = restoreRef.current
      restoreRef.current = null
      if (previous?.isConnected && typeof previous.focus === 'function') previous.focus()
    }
  }, [isOpen])

  // Autofocus the confirm button — this dialog is affirmative, not destructive.
  // When the balance is short the confirm button is `disabled`, and `.focus()` on
  // a disabled button is a no-op that would leave focus on `<body>` — outside the
  // trap, with nothing announced. Fall back to the dialog itself in that case, so
  // the shortfall message is what a screen reader reaches first.
  useEffect(() => {
    if (!isOpen) return
    const confirm = confirmRef.current
    const target = confirm && !confirm.disabled ? confirm : dialogRef.current
    target?.focus()
  }, [isOpen, insufficient])

  // Key handling scoped to the dialog node (§57), never `window` — a listener on
  // window fires alongside the focused button's own Enter activation.
  useEffect(() => {
    const node = dialogRef.current
    if (!isOpen || !node) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Escape dismisses even while loading: a confirm handler that redirects
        // away (or rejects without clearing its flag) must never leave the
        // dialog unclosable. Backdrop click and Cancel stay guarded by
        // `!loading`.
        e.stopPropagation()
        onCancel()
        return
      }
      if (e.key === 'Tab') {
        const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        e.preventDefault()
        e.stopPropagation()
        if (focusables.length === 0) return
        const current = focusables.indexOf(document.activeElement as HTMLElement)
        const next =
          current === -1
            ? (e.shiftKey ? focusables[focusables.length - 1] : focusables[0])
            : e.shiftKey
              ? focusables[(current === 0 ? focusables.length : current) - 1]
              : focusables[(current + 1) % focusables.length]
        next?.focus()
      }
    }

    node.addEventListener('keydown', onKey)
    return () => {
      node.removeEventListener('keydown', onKey)
    }
  }, [isOpen, onCancel])

  if (!isOpen || typeof document === 'undefined') return null

  const Glyph = isPurchase ? ShoppingBag : Lightning

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
      {/* Backdrop — blur-only, no dark overlay (§16, §45) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
        className="fixed top-0 left-0 w-screen h-screen"
        onClick={() => !loading && onCancel()}
        aria-hidden="true"
      />

      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        // Focusable-but-not-tabbable: clicking dead space inside the dialog
        // lands here, so the scoped keydown listener keeps seeing Escape/Tab.
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          'relative w-full max-w-md rounded-2xl border bg-card p-6 shadow-2xl outline-none',
          'border-border/60 dark:border-white/[0.08] dark:bg-[#191a1b]',
        )}
      >
        <div className="flex items-center gap-3">
          <div className={iconChipClass} aria-hidden="true">
            <Glyph className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
          </div>
          <h2
            id={titleId}
            className="text-foreground dark:text-[#f7f8f8]"
            style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
          >
            {isPurchase ? 'Confirm purchase' : 'Confirm credit spend'}
          </h2>
        </div>

        <div id={descId} className="mt-4 space-y-2">
          {/* Summary rows — label left, value right, hairline between. */}
          <dl className="rounded-lg border border-border/50 dark:border-white/[0.06] divide-y divide-border/50 dark:divide-white/[0.06]">
            {[
              { label: 'Item', value: actionLabel },
              isPurchase ? { label: 'Price', value: formatCents(priceCents as number, currency) } : null,
              typeof grantedCredits === 'number'
                ? { label: 'Credits added', value: `+${grantedCredits.toLocaleString()}` }
                : null,
              isSpend
                ? {
                    label: 'Credits used',
                    value: `~${(estimatedCredits as number).toLocaleString()}${
                      typeof estimatedTokens === 'number'
                        ? ` (~${estimatedTokens.toLocaleString()} tokens)`
                        : ''
                    }`,
                  }
                : null,
              { label: 'Balance now', value: `${currentBalance.toLocaleString()} credits` },
            ]
              .filter((row): row is { label: string; value: string } => row !== null)
              .map(({ label, value }) => (
                <div key={label} className="flex items-baseline justify-between gap-4 px-3 py-2">
                  <dt
                    className="text-muted-foreground dark:text-[#8a8f98] uppercase shrink-0"
                    style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                  >
                    {label}
                  </dt>
                  <dd
                    className="text-foreground dark:text-[#f7f8f8] text-right tabular-nums"
                    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                  >
                    {value}
                  </dd>
                </div>
              ))}
          </dl>

          {showRecurringTerms && (
            <div className={infoBannerClass} style={bannerTextStyle}>
              <span>
                {recurringInterval === 'year' ? (
                  <>
                    This starts an annual subscription. You are charged today for the whole year and
                    then once a year until you cancel — cancel any time and the plan runs to the end
                    of the year you have already paid for.
                  </>
                ) : (
                  <>
                    This starts a monthly subscription. You are charged today and then once a month
                    until you cancel — cancel any time and the plan runs to the end of the month you
                    have already paid for.
                  </>
                )}
                {detail ? <> {detail}</> : null}
              </span>
            </div>
          )}

          {isPurchase && !showRecurringTerms && (
            <div className={infoBannerClass} style={bannerTextStyle}>
              <span>
                This is a one-time charge. There is no recurring billing and no subscription to
                cancel.
                {detail ? <> {detail}</> : null}
              </span>
            </div>
          )}
          {!isPurchase && detail && (
            <p
              className="text-muted-foreground dark:text-[#8a8f98] px-0.5"
              style={{ fontSize: '12px', lineHeight: 1.55, letterSpacing: '-0.1px' }}
            >
              {detail}
            </p>
          )}
          {insufficient && (
            <div className={dangerBannerClass} style={bannerTextStyle} role="alert">
              <span>Not enough credits for this action. Top up before continuing.</span>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            ref={confirmRef}
            onClick={onConfirm}
            loading={loading}
            disabled={insufficient}
          >
            {confirmLabel ?? (isPurchase ? 'Continue to payment' : 'Confirm')}
          </Button>
        </div>
      </motion.div>
    </div>,
    document.body,
  )
}
